import {Trans} from '@lingui/macro';
import {noop} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {usePrevious} from '../../../../srcCommon/helpers/hookHelpers';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {Skeleton} from '../../../components/common/skeleton';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {UnsavedLocalApplicationModel} from '../../../models/api/localApplicationModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {
  SettingsApplicationComponentsEnum,
  SettingsApplicationsDetailsUrl,
  SettingsApplicationsUrl,
  SettingsAutolinkersUrl,
  SettingsOauthClientUrl,
  SettingsPreviewersUrl,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
  SettingsServersUrl,
} from '../../../models/url/settingsUrlModel';
import {getCompany} from '../../../store/data/company/companySelectors';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {
  useFetchInstalledApplication,
  useFetchInstalledApplications,
} from '../../fetchers/installedApplications';
import {InstalledApplicationMutationsEnum} from '../../fetchers/installedApplicationsMutations';
import {useFetchLocalApplication, useFetchLocalApplications} from '../../fetchers/localApplications';
import {LocalApplicationMutationsEnum} from '../../fetchers/localApplicationsMutations';
import {SettingsHeader} from '../../settingsHeader';
import {SettingsAutolinkers} from '../autolinkers/settingsAutolinkers';
import {useSearchQuery} from '../hooks/useSearchQuery';
import {SettingsOauthClient} from '../oauth_clients/settingsOauthClient';
import {SettingsPreviewers} from '../previewers/settingsPreviewers';
import {SettingsServers} from '../servers/settingsServers';
import {ApplicationsHeader} from './applicationsHeader';
import {ApplicationsList} from './applicationsList';
import {SettingsApplicationDetails} from './settingsApplicationDetails';
import {useApplicationsInfoBox} from './settingsApplicationsInfoBox';

/*
 * Props.
 */

interface SettingsApplicationsContainerProps {
  panelId: SettingsPanelId;
  url: SettingsApplicationsUrl;
}

interface SettingsApplicationsListContainerProps {
  panelId: SettingsPanelId;
  url: SettingsApplicationsUrl;
  onCreateApplication: (app: UnsavedLocalApplicationModel) => void;
  isCreateMutationLoading: boolean;
  isCreateMutationSuccess: boolean;
}

type SingleApplicationUrl =
  | SettingsApplicationsDetailsUrl
  | SettingsAutolinkersUrl
  | SettingsOauthClientUrl
  | SettingsPreviewersUrl
  | SettingsServersUrl;

interface SettingsSingleApplicationContainerProps {
  panelId: SettingsPanelId;
  url: SingleApplicationUrl;
  applicationId: number;
  onEditApplication: (
    app: UnsavedLocalApplicationModel,
    applicationId: number,
    applicationUrl: string,
  ) => void;
  isEditMutationLoading: boolean;
  isEditMutationSuccess: boolean;
}

interface SettingsCreateApplicationContainerProps {
  onCreateApplication: (app: UnsavedLocalApplicationModel) => void;
  panelId: SettingsPanelId;
  url: SettingsApplicationsDetailsUrl;
}

/*
 * Style.
 */

const StyledSettingsApplicationsWrapperDiv = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const StyledLoadingWrapperDiv = styled.div`
  padding: 0 48px;
`;

/*
 * Component.
 */

export const SettingsApplicationsContainer: FC<SettingsApplicationsContainerProps> = (props) => {
  const {panelId, url} = props;

  const {mutate, mutateIsLoading, mutateIsSuccess} = useFetchLocalApplications('');
  const company = useAppSelector(getCompany);

  const onCreateApplication = (unsavedApplication: UnsavedLocalApplicationModel) =>
    mutate({
      type: LocalApplicationMutationsEnum.CREATE,
      payload: {
        company,
        application: unsavedApplication,
      },
    });

  const onEditApplication = (
    unsavedApplication: UnsavedLocalApplicationModel,
    applicationId: number,
    applicationUrl: string,
  ) =>
    mutate({
      type: LocalApplicationMutationsEnum.EDIT,
      payload: {
        id: applicationId,
        url: applicationUrl,
        application: unsavedApplication,
      },
    });

  switch (url.componentPage) {
    case SettingsApplicationComponentsEnum.AUTOLINKERS:
    case SettingsApplicationComponentsEnum.DETAILS:
    case SettingsApplicationComponentsEnum.OAUTH_CLIENT:
    case SettingsApplicationComponentsEnum.PREVIEWERS:
    case SettingsApplicationComponentsEnum.SERVERS: {
      const {applicationId} = url;

      // If we have an application, we should fetch and render it.
      if (applicationId) {
        return (
          <SettingsSingleApplicationContainer
            panelId={panelId}
            url={url}
            applicationId={applicationId}
            onEditApplication={onEditApplication}
            isEditMutationLoading={mutateIsLoading}
            isEditMutationSuccess={mutateIsSuccess}
          />
        );
      }
    }
    // We want to fall through and render the list or create page.
    // eslint-disable-next-line no-fallthrough
    case SettingsApplicationComponentsEnum.LIST:
    default:
      return (
        <SettingsApplicationsListContainer
          panelId={panelId}
          url={url}
          onCreateApplication={onCreateApplication}
          isCreateMutationLoading={mutateIsLoading}
          isCreateMutationSuccess={mutateIsSuccess}
        />
      );
  }
};

const SettingsApplicationsListContainer: FC<SettingsApplicationsListContainerProps> = (props) => {
  const {url, panelId, onCreateApplication, isCreateMutationLoading, isCreateMutationSuccess} = props;

  const company = useAppSelector(getCompany);
  const {searchQuery, resetSearchQuery, onSearchQueryChange} = useSearchQuery(url);
  const {
    items: applications,
    loadMore,
    moreStatus,
    hasError,
    mutate,
    refetch: refetchInstalledApplications,
  } = useFetchInstalledApplications(searchQuery);
  const {mutate: mutateLocalApplications} = useFetchLocalApplications('');
  const {infoBox, infoBoxIcon} = useApplicationsInfoBox();

  const previousIsCreateMutationLoading = usePrevious(isCreateMutationLoading);
  useEffect(() => {
    if (previousIsCreateMutationLoading && !isCreateMutationLoading && isCreateMutationSuccess) {
      refetchInstalledApplications();
    }
  }, [
    previousIsCreateMutationLoading,
    isCreateMutationLoading,
    isCreateMutationSuccess,
    refetchInstalledApplications,
  ]);

  const {navigateToLink} = useLinkNavigation();
  const navigateToApplication = (_resourcePage: SettingsResourcePagesEnum, id?: number) => {
    if (!id) {
      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.APPLICATIONS,
          componentPage: SettingsApplicationComponentsEnum.DETAILS,
          resourcePage: SettingsResourcePagesEnum.NEW,
          applicationId: undefined,
          searchQuery: undefined,
        }),
      );
    } else {
      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.APPLICATIONS,
          componentPage: SettingsApplicationComponentsEnum.DETAILS,
          resourcePage: SettingsResourcePagesEnum.OVERVIEW,
          applicationId: id,
          searchQuery: undefined,
        }),
      );
    }
  };

  if (url.componentPage === SettingsApplicationComponentsEnum.DETAILS) {
    return (
      <SettingsCreateApplicationContainer
        panelId={panelId}
        url={url}
        onCreateApplication={onCreateApplication}
      />
    );
  }

  return (
    <>
      <SettingsHeader right={infoBoxIcon}>
        <Trans id="applications-overview-title">Applications</Trans>
      </SettingsHeader>
      <ApplicationsList
        applications={applications}
        onLoadMore={loadMore}
        moreStatus={moreStatus}
        hasError={hasError}
        onDeleteApplications={(applicationIds, applicationsToDelete) => {
          const localApplicationIds = applicationsToDelete
            .filter((application) => Boolean(application.localApplicationId))
            // These objects definitely have a localApplicationId
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .map((application) => application.localApplicationId!);

          // Delete local applications if present
          if (localApplicationIds.length) {
            mutateLocalApplications({
              type: LocalApplicationMutationsEnum.DELETE,
              payload: {
                company,
                applicationIds: localApplicationIds,
              },
            });
          }

          // Delete all installations
          mutate({
            type: InstalledApplicationMutationsEnum.DELETE,
            payload: {
              company,
              applicationIds,
            },
          });
        }}
        infoBox={infoBox}
        searchQuery={searchQuery}
        resetSearchQuery={resetSearchQuery}
        onSearchQueryChange={onSearchQueryChange}
        navigateTo={navigateToApplication}
      />
    </>
  );
};

const SettingsSingleApplicationContainer: FC<SettingsSingleApplicationContainerProps> = (props) => {
  const {panelId, url, applicationId, isEditMutationLoading, isEditMutationSuccess, onEditApplication} =
    props;

  const {
    application: installedApplication,
    status: installedApplicationStatus,
    refetch: refetchInstalledApplication,
  } = useFetchInstalledApplication(applicationId);
  const {
    application,
    status: applicationStatus,
    refetch: refetchLocalApplication,
  } = useFetchLocalApplication(installedApplication);

  const [pageTitle, setPageTitle] = useState<React.ReactNode>();
  const [pageActions, setPageActions] = useState<React.ReactNode>();

  const isLoading =
    installedApplicationStatus === AsyncStatusesEnum.LOADING ||
    applicationStatus === AsyncStatusesEnum.LOADING;

  const onRefetchData = useCallback(() => {
    refetchInstalledApplication();
    refetchLocalApplication();
  }, [refetchInstalledApplication, refetchLocalApplication]);

  const previousIsEditMutationLoading = usePrevious(isEditMutationLoading);
  useEffect(() => {
    if (previousIsEditMutationLoading && !isEditMutationLoading && isEditMutationSuccess) {
      onRefetchData();
    }
  }, [previousIsEditMutationLoading, isEditMutationLoading, isEditMutationSuccess, onRefetchData]);

  const renderPageContent = () => {
    if (isLoading) {
      return (
        <StyledLoadingWrapperDiv>
          <Skeleton width="400px" height="48px" />
        </StyledLoadingWrapperDiv>
      );
    }

    switch (url.componentPage) {
      case SettingsApplicationComponentsEnum.AUTOLINKERS:
        // The Autolinkers tab is hidden when there's no local application.
        // This should be unreachable (protected by `isLoading` above),
        // and improves typing in SettingsPreviewers.
        if (!application) {
          return null;
        }

        return (
          <SettingsAutolinkers
            application={application}
            panelId={panelId}
            url={url}
            setPageTitle={setPageTitle}
            setPageActions={setPageActions}
          />
        );

      case SettingsApplicationComponentsEnum.OAUTH_CLIENT:
        // The OAuth tab is hidden when there's no local application.
        // This should be unreachable (protected by `isLoading` above).
        if (!application) {
          return null;
        }

        return (
          <SettingsOauthClient
            application={application}
            panelId={panelId}
            url={url}
            setPageTitle={setPageTitle}
            setPageActions={setPageActions}
            onRefetchApplication={onRefetchData}
          />
        );

      case SettingsApplicationComponentsEnum.PREVIEWERS:
        // The Previewers tab is hidden when there's no local application.
        // This should be unreachable (protected by `isLoading` above),
        // and improves typing in SettingsPreviewers.
        if (!application) {
          return null;
        }

        return (
          <SettingsPreviewers
            application={application}
            panelId={panelId}
            url={url}
            setPageTitle={setPageTitle}
            setPageActions={setPageActions}
          />
        );

      case SettingsApplicationComponentsEnum.SERVERS:
        // The Servers tab is shown as long as there's an installed application.
        // This should be unreachable (protected by `isLoading` above).
        if (!installedApplication) {
          return null;
        }

        return (
          <SettingsServers
            application={application}
            installedApplication={installedApplication}
            panelId={panelId}
            url={url}
            setPageTitle={setPageTitle}
            setPageActions={setPageActions}
          />
        );

      case SettingsApplicationComponentsEnum.DETAILS:
      default:
        return (
          <SettingsApplicationDetails
            application={application}
            installedApplication={installedApplication}
            url={url}
            panelId={panelId}
            setPageTitle={setPageTitle}
            setPageActions={setPageActions}
            onEditApplication={onEditApplication}
            onCreateApplication={noop}
          />
        );
    }
  };

  return (
    <StyledSettingsApplicationsWrapperDiv>
      <ApplicationsHeader
        application={application}
        installedApplication={installedApplication}
        url={url}
        renderRight={() => (isLoading ? null : pageActions)}
      >
        {isLoading ? <Skeleton width="240px" height="31px" /> : pageTitle}
      </ApplicationsHeader>
      {renderPageContent()}
    </StyledSettingsApplicationsWrapperDiv>
  );
};

const SettingsCreateApplicationContainer: FC<SettingsCreateApplicationContainerProps> = (props) => {
  const {panelId, url, onCreateApplication} = props;

  const [pageTitle, setPageTitle] = useState<React.ReactNode>();

  return (
    <StyledSettingsApplicationsWrapperDiv>
      <ApplicationsHeader url={url}>{pageTitle}</ApplicationsHeader>
      <SettingsApplicationDetails
        url={url}
        panelId={panelId}
        setPageTitle={setPageTitle}
        setPageActions={useCallback(() => {}, [])}
        onCreateApplication={onCreateApplication}
        onEditApplication={noop}
      />
    </StyledSettingsApplicationsWrapperDiv>
  );
};
